import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { extractApiError } from '../../../../../../utils';
import {
  useCreatePrompt,
  useDeletePrompt,
  useGetExtraPrompts,
  useUpdatePrompt
} from '../../../../../../services/prompt.service';

/**
 * useFieldPrompts - Custom hook for managing prompt CRUD operations
 * @param {string} sheetId - Sheet ID
 * @param {string} categoryName - Category name
 * @param {string} tableName - Table name
 * @param {boolean} isLookup - Whether lookup table
 * @param {Object} field - Field object containing table info
 * @param {Function} setField - Function to reset field state (closes modal)
 * @returns {Object} Prompt state and handlers
 */
export const useFieldPrompts = (sheetId, categoryName, tableName, isLookup, field, setField) => {
  const [inputValues, setInputValues] = useState({});

  const prompts = useGetExtraPrompts({
    sheetId,
    params: {
      category: categoryName,
      table: isLookup ? null : tableName
    },
    queryOptions: {
      enabled: !!sheetId,
      onSuccess: res => {
        const values = {};
        if (Array.isArray(res)) {
          res.forEach(item => {
            values[item.field_name] = item.prompt_text;
          });
        }
        setInputValues(values);
      }
    }
  });

  const createPrompt = useCreatePrompt({
    params: {
      sheet_id: sheetId
    }
  });

  const deletePrompt = useDeletePrompt();

  const updatePrompt = useUpdatePrompt({
    params: {
      sheet_id: sheetId
    }
  });

  const getPromptValue = useCallback(
    (columnName, _tableName) => {
      if (Array.isArray(prompts?.data)) {
        return prompts.data.find(
          value =>
            value.field_name === columnName &&
            value.category === categoryName &&
            value.table === (_tableName || tableName)
        );
      }
      return undefined;
    },
    [prompts?.data, categoryName, tableName]
  );

  const onCreatePrompt = useCallback(
    (value, columnName, _tableName, successFn) => {
      if (value)
        createPrompt.mutate(
          {
            prompt_text: value,
            category: categoryName,
            table: isLookup ? field.table : _tableName || tableName,
            field_name: columnName
          },
          {
            onSuccess: () => {
              setField({});
              prompts.refetch();
              setInputValues({});
              if (typeof successFn === 'function') successFn();
              toast.success('Prompt added successfully!');
            },
            onError: err => {
              toast.error(extractApiError(err, 'Unable to add this prompt.'));
            }
          }
        );
    },
    [createPrompt, categoryName, tableName, isLookup, field, prompts, setField]
  );

  const onDeletePrompt = useCallback(
    (id, successFn) => {
      deletePrompt.mutate(
        {
          id
        },
        {
          onSuccess: () => {
            prompts.refetch();
            setInputValues({});
            if (typeof successFn === 'function') successFn();
            toast.success('Prompt deleted successfully!');
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to delete this prompt.'));
          }
        }
      );
    },
    [deletePrompt, prompts]
  );

  const onUpdatePrompt = useCallback(
    (id, value, columnName, successFn) => {
      updatePrompt.mutate(
        {
          id,
          data: {
            prompt_text: value,
            category: categoryName,
            table: tableName,
            field_name: columnName
          }
        },
        {
          onSuccess: () => {
            setField({});
            prompts.refetch();
            setInputValues({});
            if (typeof successFn === 'function') successFn();
            toast.success('Prompt updated successfully!');
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to update this prompt.'));
          }
        }
      );
    },
    [updatePrompt, categoryName, tableName, isLookup, field, prompts, setField]
  );

  return {
    prompts,
    promptsLoading: prompts.isLoading,
    createLoading: createPrompt.isLoading,
    updateLoading: updatePrompt.isLoading,
    deleteLoading: deletePrompt.isLoading,
    getPromptValue,
    onCreatePrompt,
    onDeletePrompt,
    onUpdatePrompt,
    inputValues
  };
};
